"use client";

import { useEffect, useState } from "react";
import { subscribePush } from "@/app/actions/account";
import { useLang } from "./lang";

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/\//g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

function isHttpsApp() {
  return window.isSecureContext && window.location.protocol === "https:";
}

export function PwaRegister() {
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const run = async () => {
      const allowSw = process.env.NODE_ENV === "production" && isHttpsApp();
      if (!allowSw) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
        if ("caches" in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
        return;
      }
      const host = window.location.pathname.startsWith("/host");
      await navigator.serviceWorker.register(host ? "/host-sw.js" : "/sw.js", {
        scope: host ? "/host" : "/",
        updateViaCache: "none",
      });
    };

    run().catch(() => undefined);
  }, []);
  return null;
}

export function InstallBanner() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [ios, setIos] = useState(false);
  const [standalone, setStandalone] = useState(true);

  useEffect(() => {
    setStandalone(window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true);
    setIos(/iphone|ipad|ipod/i.test(navigator.userAgent));
    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const { t } = useLang();
  if (standalone) return null;

  return (
    <div className="card mx-4 mb-4 p-4 flex items-start gap-3">
      <div className="flex-1">
        <p className="display text-lg">{t("installTitle")}</p>
        <p className="text-sm text-muted mt-1">{ios ? t("installIos") : t("installOther")}</p>
      </div>
      {deferred && (
        <button
          className="btn btn-primary text-sm"
          onClick={async () => {
            await deferred.prompt();
            setDeferred(null);
          }}
        >
          {t("install")}
        </button>
      )}
    </div>
  );
}

export function PushToggle() {
  const [state, setState] = useState<"off" | "on" | "denied" | "unsupported">("off");

  useEffect(() => {
    if (!("Notification" in window) || !("serviceWorker" in navigator)) {
      setState("unsupported");
      return;
    }
    if (Notification.permission === "denied") setState("denied");
    navigator.serviceWorker.ready.then((reg) =>
      reg.pushManager.getSubscription().then((s) => {
        if (s) setState("on");
      }),
    );
  }, []);

  async function enable() {
    const key = process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY;
    if (!key) return;
    const perm = await Notification.requestPermission();
    if (perm !== "granted") {
      setState("denied");
      return;
    }
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key),
    });
    const json = sub.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;
    await subscribePush({ endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } });
    setState("on");
  }

  const { t } = useLang();
  if (state === "unsupported") return <p className="text-sm text-muted">{t("pushUnsupported")}</p>;
  if (state === "denied") return <p className="text-sm text-muted">{t("pushDenied")}</p>;

  return (
    <button className="btn btn-ghost w-full" onClick={enable} type="button">
      {state === "on" ? t("pushOn") : t("pushOff")}
    </button>
  );
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
}
